import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { List } from 'react-window';
import { leadTemps, legacyLeadTemps, APPOINTMENT_FILTER } from '../mock';
import { useLeads, LeadsStore } from '../lib/leadsStore';
import { addressIncludes } from '../lib/addressMatch';
import { TeamStore, useTeam } from '../lib/teamStore';
import {
  Flame, Sun, Snowflake, PhoneCall, Sprout, Ban, XCircle,
  Plus, Filter, Search, X, Archive, ShieldX,
  CalendarCheck, BadgeCheck, Target, Megaphone, CalendarClock, ChevronDown,
} from 'lucide-react';
import { cn, formatPhone } from '../lib/utils';
import CreateLeadModal from '../components/leads/CreateLeadModal';
import LeadDetailPanel from '../components/leads/LeadDetailPanel';
import { useAuth } from '../lib/authStore';
import { LOAN_STAGE_KEYS } from '../components/LoanStageBadge';

// Once a lead's `loanStatus` enters any of these stages, it has been
// "promoted" to the Pipeline page and disappears from /leads.
const PIPELINE_STAGE_SET = new Set([
  ...LOAN_STAGE_KEYS,
  // Legacy keys handled defensively in case any older lead docs still
  // carry the pre-rebrand values.
  'submitted',
  'conditionally_approved',
]);

// Icon map covers BOTH the active pipeline statuses (hot / appointment /
// pre_approved / strike_rate / second_voice) AND the legacy values
// (follow_up / nurture / dnc / dead) so older lead records still render
// their pill correctly even though those keys no longer appear in the
// filter chip strip or status picker.
const TEMP_ICONS = {
  hot: Flame,
  appointment: CalendarCheck,
  pre_approved: BadgeCheck,
  strike_rate: Target,
  second_voice: Megaphone,
  // legacy
  follow_up: PhoneCall,
  warm: Sun,
  cold: Snowflake,
  nurture: Sprout,
  dnc: Ban,
  dead: XCircle,
};
const TEMP_STYLES = {
  hot:          { chip: 'bg-red-50 text-red-700 border-red-200',           dot: 'bg-red-500',     accent: 'text-red-500' },
  appointment:  { chip: 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40', dot: 'bg-[hsl(var(--kf-gold))]', accent: 'text-[hsl(var(--kf-gold-deep))]' },
  pre_approved: { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', accent: 'text-emerald-500' },
  strike_rate:  { chip: 'bg-sky-50 text-sky-700 border-sky-200',           dot: 'bg-sky-500',     accent: 'text-sky-500' },
  second_voice: { chip: 'bg-violet-50 text-violet-700 border-violet-200',  dot: 'bg-violet-500',  accent: 'text-violet-500' },
  // legacy
  follow_up:    { chip: 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/40', dot: 'bg-[hsl(var(--kf-gold))]', accent: 'text-[hsl(var(--kf-gold-deep))]' },
  warm:         { chip: 'bg-orange-50 text-orange-700 border-orange-200',  dot: 'bg-orange-400',  accent: 'text-orange-500' },
  cold:         { chip: 'bg-sky-50 text-sky-700 border-sky-200',           dot: 'bg-sky-400',     accent: 'text-sky-500' },
  nurture:      { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', accent: 'text-emerald-500' },
  dnc:          { chip: 'bg-neutral-200 text-neutral-700 border-neutral-300', dot: 'bg-neutral-500', accent: 'text-neutral-500' },
  dead:         { chip: 'bg-neutral-100 text-neutral-500 border-neutral-200', dot: 'bg-neutral-400', accent: 'text-neutral-400' },
};

// Convenience for the row badge — legacy values render with their pill
// but aren't selectable from the picker. Used by both the LeadRow and
// the StatusMenu in LeadProfile.
export const ALL_TEMP_LABELS = Object.fromEntries(
  [...leadTemps, ...legacyLeadTemps].map((t) => [t.key, t.label])
);

function money(n) {
  if (!n) return '—';
  return `$${Number(n).toLocaleString()}`;
}

// Pull a 2-letter uppercase initials pair from a borrower name. Falls
// back to '?' when the name is blank so the avatar still renders a
// stable black circle instead of collapsing. Deliberately duplicates
// the backend `_initials()` logic so Outbound-imported leads whose
// `lead.initials` field is still null (schema introduced after their
// import) render correctly without a one-time backfill.
function initialsOf(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Interpret a rate value the way an underwriter would: null/undefined
// clearly means "not set yet", but ARIVE / outbound imports frequently
// land a literal `0` for the same reason. Both collapse to the em-dash
// treatment so the broker doesn't read a bogus "0.000%" on an unworked
// lead. Legitimate zero rates don't exist in mortgage lending.
function fmtRate(raw) {
  if (raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${n.toFixed(3)}%`;
}

// Pick the first non-zero loan amount from the places the schema can
// stash it: `estAmount` (CRM canonical), `application.loanAmount`
// (ARIVE pull), and `application.purchasePrice` (purchase-only
// imports). Zero/empty strings count as missing — same reasoning as
// `fmtRate` above.
function pickLoanAmount(lead) {
  const app = lead?.application || {};
  const candidates = [lead?.estAmount, app.loanAmount, app.purchasePrice];
  for (const c of candidates) {
    if (c == null || c === '') continue;
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

// Canonical loan purposes (Feb 12, 2026 — user-defined). Dropdowns everywhere
// now use this list. Legacy values stored in older leads ("Refi", "Cash Out
// Refi", "Rate & Term Refi") get mapped to the canonical labels at display
// time so the table reads consistently without forcing a one-time migration.
export const LOAN_PURPOSES = [
  'Purchase',
  'Refinance',
];

function formatLoanPurpose(raw) {
  if (!raw) return '—';
  const s = String(raw).trim();
  const lc = s.toLowerCase();
  if (lc === 'purchase') return 'Purchase';
  // All refi variants (rate & term, cash out, HELOC, home equity, etc.) roll
  // up into the single "Refinance" label. ARIVE differentiates these via
  // separate fields (refinanceType, mortgageType=HELOC, etc.).
  if ([
    'refi', 'refinance', 'refinance r&t', 'rate & term refi', 'rate and term refi',
    'cash out refi', 'cash-out refi', 'cash out refinance',
    'heloc', 'home equity (heloc)', 'home equity loan',
  ].includes(lc)) return 'Refinance';
  return s;
}

// Pull a 2-letter US state code from anywhere we can find it on the lead.
function deriveState(lead) {
  const app = lead?.application || {};
  if (app.propertyState) return String(app.propertyState).toUpperCase().slice(0, 2);
  if (app.currentState) return String(app.currentState).toUpperCase().slice(0, 2);
  // Fallback: parse trailing ", XX" out of lead.city like "Austin, TX".
  const m = String(lead?.city || '').match(/,\s*([A-Z]{2})\s*$/i);
  if (m) return m[1].toUpperCase();
  return '—';
}

function formatApptShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  // Kinfolk is Arizona-based — every appointment time renders in
  // America/Phoenix (MST, no DST) so LOs never see a follow-up chip
  // that disagrees with the actual office clock.
  const TZ = 'America/Phoenix';
  const dayFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ });
  const now = new Date();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const dKey = dayFmt.format(d);
  const time = d.toLocaleTimeString('en-US', { timeZone: TZ, hour: 'numeric', minute: '2-digit' });
  if (dKey === dayFmt.format(now)) return `Today · ${time}`;
  if (dKey === dayFmt.format(tomorrow)) return `Tomorrow · ${time}`;
  return `${d.toLocaleDateString('en-US', { timeZone: TZ, month: 'short', day: 'numeric' })} · ${time}`;
}

export default function Leads() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const leads = useLeads();
  const teamMembers = useTeam();  // ensure team list is loaded so assignee chips can resolve
  const [filter, setFilter] = useState('all');
  // Column-sort state — null means natural priority order (appointment-today
  // first, then created-at). Click a header to override; click again to flip.
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  // Seed the search box from the URL on first mount AND keep it synced when
  // the user navigates here from elsewhere (e.g. the TopBar search submits
  // /leads?q=...). Typing in the box also pushes back to the URL so the
  // search is shareable / bookmarkable.
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  useEffect(() => {
    const urlQ = searchParams.get('q') || '';
    if (urlQ !== query) setQuery(urlQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    const urlQ = searchParams.get('q') || '';
    if (query === urlQ) return;
    const next = new URLSearchParams(searchParams);
    if (query) next.set('q', query);
    else next.delete('q');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);
  const [createOpen, setCreateOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Right-side detail panel state. `selectedLeadId` is kept as an id so
  // when the leads list refreshes (new note, status change, etc.) the
  // panel always reflects the LATEST version of the lead instead of a
  // stale snapshot. Lookup happens in render.
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Advanced filters (from the Filters popover)
  const [adv, setAdv] = useState({
    states: [],           // multi-select array of state codes
    purposes: [],         // multi-select array of canonical loan purposes
    minAmount: '',        // string in the input, parsed to number when filtering
    maxAmount: '',
    showWithdrawn: false, // when true, ONLY show withdrawn; when false, hide them
    showDenied: false,    // when true, ONLY show denied; when false, hide them
  });
  const advCount =
    (adv.states.length ? 1 : 0) +
    (adv.purposes.length ? 1 : 0) +
    (adv.minAmount || adv.maxAmount ? 1 : 0) +
    (adv.showWithdrawn ? 1 : 0) +
    (adv.showDenied ? 1 : 0);
  const resetAdv = () => setAdv({ states: [], purposes: [], minAmount: '', maxAmount: '', showWithdrawn: false, showDenied: false });

  // Wingman can fire `wingman:filter` events to drive the page,
  // OR set a pending filter in LeadsStore right before navigating here (NAV+FILTER race).
  useEffect(() => {
    // Consume any pending filter that was queued while we were unmounted.
    const pending = LeadsStore.consumePendingFilter?.();
    if (pending) setFilter(pending);

    const onWingmanFilter = (e) => {
      const v = e?.detail?.bucket;
      if (v) setFilter(v);
    };
    window.addEventListener('wingman:filter', onWingmanFilter);
    return () => window.removeEventListener('wingman:filter', onWingmanFilter);
  }, []);

  // "Follow Up Today" is now driven by `appointmentAt` (date today, not yet passed).
  const isToday = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    return d.toDateString() === now.toDateString();
  };

  // ---- Scope (My / All) ----
  // Only admins can flip to "All Leads"; loan officers are locked to their
  // own pipeline. Even if an LO crafts a `?scope=all` URL we collapse it
  // back to "my" both in the read and on a subsequent write below.
  const { user } = useAuth();
  const CURRENT_LO_ID = user?.id || null;
  const isAdmin = user?.role === 'admin';
  const rawScope = (searchParams.get('scope') === 'global' || searchParams.get('scope') === 'all') ? 'all' : 'my';
  const scope = isAdmin ? rawScope : 'my';
  const setScope = (next) => {
    if (!isAdmin) return; // hard guard: LOs can't switch scopes
    const ps = new URLSearchParams(searchParams);
    if (next === 'all') ps.set('scope', 'all'); else ps.delete('scope');
    setSearchParams(ps, { replace: true });
  };
  // Strip a stale ?scope=all off the URL the moment an LO lands here so
  // back-button history doesn't leak the admin view.
  useEffect(() => {
    if (!isAdmin && (searchParams.get('scope') === 'global' || searchParams.get('scope') === 'all')) {
      const ps = new URLSearchParams(searchParams);
      ps.delete('scope');
      setSearchParams(ps, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, searchParams]);

  // Admin-only filter: when viewing All Leads, restrict to a specific LO.
  // `all` = show every LO's pipeline. `unassigned` = no assignee. Otherwise
  // matches the assignee uuid. Only available in the All Leads scope.
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  useEffect(() => {
    if (scope === 'my' && assigneeFilter !== 'all') setAssigneeFilter('all');
  }, [scope, assigneeFilter]);

  // Visible leads = active by default. Toggles for withdrawn/denied flip into
  // single-status views. (If both are on, we union them — viewing closed work.)
  const visibleLeads = useMemo(() => {
    // Pipeline-stage leads have been "promoted" out of /leads and live on
    // the Pipeline page. Hide them so the broker's lead list stays focused
    // on what still needs courting.
    let pool = leads.filter((l) => {
      const stage = l.loanStatus;
      if (!stage) return true;
      return !PIPELINE_STAGE_SET.has(stage);
    });
    if (scope === 'my') {
      pool = pool.filter((l) => l.assignedTo && l.assignedTo === CURRENT_LO_ID);
    } else if (assigneeFilter !== 'all') {
      // Admin is in "All Leads" view but has scoped to one LO (or unassigned).
      if (assigneeFilter === 'unassigned') {
        pool = pool.filter((l) => !l.assignedTo);
      } else {
        pool = pool.filter((l) => l.assignedTo === assigneeFilter);
      }
    }
    const showW = adv.showWithdrawn;
    const showD = adv.showDenied;
    if (showW && showD) return pool.filter((l) => l.withdrawn || l.denied);
    if (showW) return pool.filter((l) => !!l.withdrawn);
    if (showD) return pool.filter((l) => !!l.denied);
    return pool.filter((l) => !l.withdrawn && !l.denied);
  }, [leads, scope, assigneeFilter, adv.showWithdrawn, adv.showDenied, CURRENT_LO_ID]);

  const counts = useMemo(() => {
    const now = Date.now();
    // For the "All" chip we count leads that would actually appear —
    // that means DNC leads are excluded (they only surface under the
    // dedicated Opt Out chip / Reports).
    const activePool = visibleLeads.filter((l) => l.temp !== 'dnc');
    const c = { all: activePool.length };
    leadTemps.forEach((t) => {
      c[t.key] = visibleLeads.filter((l) => l.temp === t.key).length;
    });
    // "Appointment" is a derived chip — count leads with a future
    // appointment regardless of their temp.
    c.appointment = activePool.filter((l) => {
      if (!l.appointmentAt) return false;
      const t = new Date(l.appointmentAt).getTime();
      return !Number.isNaN(t) && t >= now - 60 * 60 * 1000; // grace 1h after start
    }).length;
    // Subset: appointments specifically falling on TODAY — used by the
    // header subtitle and the "Follow up today" pill sort.
    c.appointmentToday = activePool.filter((l) => isToday(l.appointmentAt)).length;
    return c;
  }, [visibleLeads]);

  // Build the State/Purpose option lists from the visible pool so the popover
  // never shows a stale value that filters to zero.
  const stateOptions = useMemo(() => {
    const set = new Set();
    visibleLeads.forEach((l) => {
      const s = deriveState(l);
      if (s && s !== '—') set.add(s);
    });
    return Array.from(set).sort();
  }, [visibleLeads]);
  const purposeOptions = useMemo(() => {
    const set = new Set();
    visibleLeads.forEach((l) => {
      const p = formatLoanPurpose(l.application?.loanPurpose || l.loanIntent);
      if (p && p !== '—') set.add(p);
    });
    return Array.from(set).sort();
  }, [visibleLeads]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    const min = adv.minAmount ? Number(String(adv.minAmount).replace(/[^0-9.]/g, '')) : null;
    const max = adv.maxAmount ? Number(String(adv.maxAmount).replace(/[^0-9.]/g, '')) : null;
    const now = Date.now();
    const list = visibleLeads.filter((l) => {
      // Opt-out isolation: once a lead is opted out (temp='dnc'), yank
      // them from EVERY primary-CRM filter. The dedicated Opt Out chip
      // is the only view that surfaces them (mirrored in AdminReports).
      if (l.temp === 'dnc' && filter !== 'dnc') return false;
      // Chip filter — "appointment" is special: any lead with a future
      // (or just-started) appointmentAt qualifies, irrespective of temp.
      // Every other chip key maps to a real `temp` value.
      let matchTemp;
      if (filter === 'all') matchTemp = true;
      else if (filter === 'appointment') {
        if (!l.appointmentAt) matchTemp = false;
        else {
          const t = new Date(l.appointmentAt).getTime();
          matchTemp = !Number.isNaN(t) && t >= now - 60 * 60 * 1000;
        }
      } else matchTemp = l.temp === filter;
      if (!matchTemp) return false;

      // Search — covers name, city, email, phone (digits + raw),
      // loan intent, state, AND the 1003 property/current addresses so
      // typing a street name / zip from the listing pulls the right lead.
      if (q) {
        const app = l.application || {};
        const phoneDigits = (l.phone || '').replace(/\D/g, '');
        const qDigits = q.replace(/\D/g, '');
        const matchQ =
          l.name.toLowerCase().includes(q) ||
          (l.city || '').toLowerCase().includes(q) ||
          (l.email || '').toLowerCase().includes(q) ||
          (l.phone || '').toLowerCase().includes(q) ||
          (qDigits && phoneDigits.includes(qDigits)) ||
          (l.loanIntent || '').toLowerCase().includes(q) ||
          deriveState(l).toLowerCase().includes(q) ||
          (app.firstName || '').toLowerCase().includes(q) ||
          (app.lastName || '').toLowerCase().includes(q) ||
          (app.propertyAddress || '').toLowerCase().includes(q) ||
          (app.propertyCity || '').toLowerCase().includes(q) ||
          (app.propertyZip || '').toLowerCase().includes(q) ||
          (app.currentAddress || '').toLowerCase().includes(q) ||
          (app.currentCity || '').toLowerCase().includes(q) ||
          (app.currentZip || '').toLowerCase().includes(q) ||
          // Directional-aware fallback so "W Yellowstone" still finds
          // records stored as "West Yellowstone" (and vice versa).
          addressIncludes(app.propertyAddress, q) ||
          addressIncludes(app.currentAddress, q);
        if (!matchQ) return false;
      }

      // Advanced filters
      if (adv.states.length) {
        if (!adv.states.includes(deriveState(l))) return false;
      }
      if (adv.purposes.length) {
        const p = formatLoanPurpose(l.application?.loanPurpose || l.loanIntent);
        if (!adv.purposes.includes(p)) return false;
      }
      if (min != null && !Number.isNaN(min)) {
        if ((l.estAmount || 0) < min) return false;
      }
      if (max != null && !Number.isNaN(max)) {
        if ((l.estAmount || 0) > max) return false;
      }
      return true;
    });
    // When viewing the Appointment chip, sort by next-closest first
    // (ascending appointmentAt) so the most-imminent meeting is on top.
    if (filter === 'appointment') {
      list.sort((a, b) => new Date(a.appointmentAt).getTime() - new Date(b.appointmentAt).getTime());
    } else {
      // For every other view (All, Hot, Pre-Approved, etc.) leads with an
      // appointment today float to the very top so the LO sees their
      // "Follow up today" queue first.
      list.sort((a, b) => {
        const aT = isToday(a.appointmentAt) ? new Date(a.appointmentAt).getTime() : Number.POSITIVE_INFINITY;
        const bT = isToday(b.appointmentAt) ? new Date(b.appointmentAt).getTime() : Number.POSITIVE_INFINITY;
        return aT - bT;
      });
    }
    // User-selected column sort overrides the default priority order.
    if (sort.key) {
      const getters = {
        name:        (l) => (l.name || '').toLowerCase(),
        phone:       (l) => (l.phone || '').replace(/\D/g, ''),
        email:       (l) => (l.email || '').toLowerCase(),
        loanPurpose: (l) => formatLoanPurpose(l.application?.loanPurpose || l.loanIntent).toLowerCase(),
        loanType:    (l) => (l.application?.loanType || '').toLowerCase(),
        loanAmount:  (l) => pickLoanAmount(l) || 0,
        rate:        (l) => {
          const v = l.application?.rate ?? l.application?.loanRate;
          const n = Number(v);
          return Number.isFinite(n) && n > 0 ? n : 0;
        },
        state:       (l) => (deriveState(l) || '').toLowerCase(),
        status:      (l) => {
          // Surface order: temp label > follow-up > withdrawn/denied > none.
          if (l.temp) return l.temp;
          if (l.appointmentAt && isToday(l.appointmentAt)) return 'appointment';
          if (l.withdrawn) return 'zz_withdrawn';
          if (l.denied) return 'zz_denied';
          return 'zzz';
        },
        time:        (l) => l.appointmentAt ? new Date(l.appointmentAt).getTime() : Number.POSITIVE_INFINITY,
      };
      const get = getters[sort.key];
      if (get) {
        const dir = sort.dir === 'asc' ? 1 : -1;
        list.sort((a, b) => {
          const av = get(a);
          const bv = get(b);
          if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
          if (!av && bv) return 1;
          if (av && !bv) return -1;
          return String(av).localeCompare(String(bv)) * dir;
        });
      }
    }
    return list;
  }, [filter, query, visibleLeads, adv, sort]);

  // Resolve the selected lead doc from the live `leads` array so updates
  // (new notes, status changes, etc.) flow into the panel automatically.
  const selectedLead = useMemo(
    () => (selectedLeadId ? leads.find((l) => l.id === selectedLeadId) || null : null),
    [leads, selectedLeadId],
  );
  const panelOpen = !!selectedLead;

  return (
    // Workspace layout — `items-start` so the right-side panel can be
    // `xl:sticky xl:top-16` without stretching vertically to match the
    // left column's growing height. The aside owns its own scroll area;
    // scrolling the lead list never pulls the borrower summary off-
    // screen.
    <div className="flex flex-col xl:flex-row gap-0 min-h-full xl:items-start">
      {/* ── LEFT: existing leads workspace. When the right-side panel is
          open, cap the width so the row grid doesn't crush the panel. */}
      <div className={cn('flex-1 min-w-0', panelOpen ? 'xl:pr-0' : '')}>
        <div className={cn('space-y-5 mx-auto', panelOpen ? 'max-w-none' : 'max-w-[1500px]')}>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-400 font-bold">Workspace · Leads</div>
          <h1 className="display text-[34px] font-bold tracking-tight text-neutral-900 mt-1 leading-none">
            {scope === 'all' ? 'All Leads' : 'My Leads'}
          </h1>
          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border hairline bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700">
              <span className="h-1.5 w-1.5 rounded-full bg-neutral-900" />
              <span className="display font-bold text-neutral-900">{counts.all}</span> active
            </span>
            {counts.hot > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[11px] font-semibold text-red-700">
                <Flame className="h-3 w-3" />
                <span className="display font-bold">{counts.hot}</span> hot
              </span>
            )}
            {counts.appointmentToday > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--kf-gold))]/40 bg-[hsl(var(--kf-gold-soft))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--kf-gold-deep))]">
                <CalendarClock className="h-3 w-3" />
                <span className="display font-bold">{counts.appointmentToday}</span> follow-up{counts.appointmentToday === 1 ? '' : 's'} today
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 relative">
          {/* My / All scope toggle — admin only. LOs only ever see their own
              pipeline so the segmented control is hidden for them. */}
          {isAdmin && (
            <div className="flex items-center rounded-lg border hairline p-0.5 bg-neutral-50/70">
              {[
                { key: 'my',  label: 'My Leads' },
                { key: 'all', label: 'All Leads' },
              ].map((opt) => {
                const active = scope === opt.key;
                return (
                  <button
                    key={opt.key}
                    data-testid={`leads-scope-${opt.key}`}
                    onClick={() => setScope(opt.key)}
                    className={cn(
                      'h-8 px-3 rounded-md text-[12px] font-bold transition flex items-center gap-1.5',
                      active
                        ? 'bg-neutral-900 text-white shadow-[0_2px_8px_-4px_rgba(0,0,0,0.4)]'
                        : 'text-neutral-600 hover:text-neutral-900'
                    )}
                    title={opt.key === 'my' ? 'Only leads assigned to you' : "Search across every LO's pipeline"}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}
          {/* LO scoping dropdown — only when an admin is viewing All Leads.
              Lets the admin drill into one broker's pipeline (or the
              unassigned bucket) without leaving the page. */}
          {isAdmin && scope === 'all' && (
            <AssigneeFilter
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              members={teamMembers}
            />
          )}
          <button
            data-testid="leads-filters-trigger"
            onClick={() => setFiltersOpen((v) => !v)}
            className={cn(
              'h-9 px-3 rounded-lg border hairline text-[12.5px] font-semibold flex items-center gap-1.5 transition',
              filtersOpen
                ? 'bg-neutral-900 text-white border-neutral-900'
                : advCount > 0
                  ? 'bg-[hsl(var(--kf-gold-soft))] border-[hsl(var(--kf-gold))]/50 text-[hsl(var(--kf-gold-deep))]'
                  : 'bg-white hover:border-neutral-400 text-neutral-700'
            )}
          >
            <Filter className="h-3.5 w-3.5" /> Filters
            {advCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-neutral-900 text-white text-[10px] font-bold px-1">{advCount}</span>
            )}
          </button>
          <button
            data-testid="create-lead-trigger"
            onClick={() => setCreateOpen(true)}
            className="h-9 px-3 rounded-lg btn-ink text-[12.5px] font-semibold flex items-center gap-1.5"
          >
            <Plus className="h-4 w-4" /> Create Lead
          </button>
          {filtersOpen && (
            <FiltersPopover
              adv={adv}
              setAdv={setAdv}
              stateOptions={stateOptions}
              purposeOptions={purposeOptions}
              onClose={() => setFiltersOpen(false)}
              onReset={resetAdv}
            />
          )}
        </div>
      </div>

      {/* Toolbar — search + filter chips (single source of truth for buckets) */}
      <div className="rounded-2xl border hairline bg-white px-4 py-3 flex flex-wrap items-center gap-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            data-testid="leads-page-search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              // Typing in the in-page search box explicitly drops out of global
              // scope — this input is "my pipeline only", per the spec. The
              // segmented control + the TopBar/Sidebar global search remain
              // the two ways to escape this.
              if (scope === 'all') setScope('my');
            }}
            placeholder="Search my pipeline by name, phone, email, or property address…"
            className="w-full h-11 pl-10 pr-3 rounded-lg bg-neutral-50/70 border hairline text-[14px] placeholder:text-neutral-400 focus:outline-none focus:bg-white focus:border-neutral-900 transition"
          />
        </div>
        <div className="flex items-center gap-1 rounded-lg border hairline p-1 bg-neutral-50/70 overflow-x-auto">
          <button
            data-testid="leads-filter-all"
            onClick={() => setFilter('all')}
            className={cn(
              'h-9 text-[12.5px] font-semibold px-3 rounded-md whitespace-nowrap transition flex items-center gap-1.5',
              filter === 'all' ? 'bg-neutral-900 text-white shadow-sm' : 'text-neutral-600 hover:text-neutral-900 hover:bg-white'
            )}
          >
            All <span className={cn('mono text-[10.5px] tabular-nums', filter === 'all' ? 'text-white/60' : 'text-neutral-400')}>{counts.all}</span>
          </button>
          {/* Build the chip strip: All, Hot, Appointment (special),
              Pre-Approved, Strike Rate, 2nd Voice. The Appointment chip
              is positioned after Hot per the broker workflow (most-used
              second only to Hot) but uses derived filtering, not temp. */}
          {(() => {
            const chips = [];
            const insert = (t) => chips.push(t);
            leadTemps.forEach((t) => {
              insert(t);
              // After Hot, drop in the derived Appointment chip.
              if (t.key === 'hot') insert(APPOINTMENT_FILTER);
            });
            return chips.map((t) => {
              const Icon = TEMP_ICONS[t.key];
              const active = filter === t.key;
              return (
                <button
                  data-testid={`leads-filter-${t.key}`}
                  key={t.key}
                  onClick={() => setFilter(t.key)}
                  className={cn(
                    'h-9 text-[12.5px] font-semibold px-3 rounded-md flex items-center gap-1.5 whitespace-nowrap transition',
                    active ? 'bg-neutral-900 text-white shadow-sm' : 'text-neutral-600 hover:text-neutral-900 hover:bg-white'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" /> {t.label}
                  <span className={cn('mono text-[10.5px] tabular-nums', active ? 'text-white/60' : 'text-neutral-400')}>{counts[t.key] || 0}</span>
                </button>
              );
            });
          })()}
        </div>
      </div>

        {/* Leads list — bordered rounded card per row with spacing in
            between, matching the Dancing Shoes queue. The outer container
            is transparent so each row owns its own border + shadow. */}
        <div>
          <LeadsTableHeader filter={filter} sort={sort} setSort={setSort} />
          <VirtualLeadRows
            leads={filtered}
            scope={scope}
            currentLoId={CURRENT_LO_ID}
            filter={filter}
            selectedLeadId={selectedLeadId}
            onSelect={setSelectedLeadId}
          />
        </div>

        <CreateLeadModal open={createOpen} onClose={() => setCreateOpen(false)} />
        </div>
      </div>

      {/* ── RIGHT: borrower summary panel. Rendered conditionally so it
          only takes space when something's selected. The LeadDetailPanel
          component itself handles its own fixed-overlay on mobile. */}
      {panelOpen && (
        <LeadDetailPanel
          lead={selectedLead}
          me={null}
          onClose={() => setSelectedLeadId(null)}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   VirtualLeadRows — swaps a plain map()-render for a react-window
   List once the filtered set is large enough that mounting every
   row hurts the DOM (>100 rows). Small filters still render as a
   simple flow so the page height auto-fits without a scrollbar
   inside a scrollbar.
   Row height is locked to 44px (row content 32px + py-2 = 40px +
   1px border). If the row markup grows a hair taller the row will
   crop slightly — bump ROW_HEIGHT here to match.
   ──────────────────────────────────────────────────────────────── */
const ROW_HEIGHT = 60;
const VIRTUAL_THRESHOLD = 100;

function VirtualLeadRows({ leads, scope, currentLoId, filter, selectedLeadId, onSelect }) {
  if (leads.length === 0) {
    return (
      <div className="px-5 py-16 text-center text-neutral-500 text-[13px]">No leads match this filter.</div>
    );
  }
  if (leads.length <= VIRTUAL_THRESHOLD) {
    return (
      <div className="space-y-2">
        {leads.map((l) => (
          <LeadRow
            key={l.id}
            lead={l}
            scope={scope}
            currentLoId={currentLoId}
            filter={filter}
            isSelected={selectedLeadId === l.id}
            onSelect={onSelect}
          />
        ))}
      </div>
    );
  }
  return (
    <VirtualLeadRowsInner
      leads={leads}
      scope={scope}
      currentLoId={currentLoId}
      filter={filter}
      selectedLeadId={selectedLeadId}
      onSelect={onSelect}
    />
  );
}

function VirtualLeadRowsInner({ leads, scope, currentLoId, filter, selectedLeadId, onSelect }) {
  // Height is viewport-driven so the list scrolls INSIDE the card and
  // the LO always sees the filter chips + create button at the top of
  // the page. Falls back to 600px on SSR / no-window scenarios.
  const [listHeight, setListHeight] = useState(() =>
    typeof window !== 'undefined' ? Math.max(400, window.innerHeight - 340) : 600
  );
  useEffect(() => {
    const onResize = () => setListHeight(Math.max(400, window.innerHeight - 340));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const rowKey = useCallback((index) => leads[index]?.id ?? index, [leads]);
  const rowProps = useMemo(
    () => ({ leads, scope, currentLoId, filter, selectedLeadId, onSelect }),
    [leads, scope, currentLoId, filter, selectedLeadId, onSelect],
  );

  return (
    <List
      rowComponent={VirtualLeadRow}
      rowCount={leads.length}
      rowHeight={ROW_HEIGHT}
      rowProps={rowProps}
      rowKey={rowKey}
      overscanCount={8}
      style={{ height: listHeight }}
    />
  );
}

function VirtualLeadRow({ index, style, leads, scope, currentLoId, filter, selectedLeadId, onSelect }) {
  const lead = leads[index];
  if (!lead) return null;
  // react-window locks each slot to ROW_HEIGHT; the inner card is
  // rendered with a bottom gap so visible spacing between cards matches
  // the non-virtualized path's `space-y-2`.
  return (
    <div style={style} className="pb-2">
      <LeadRow
        lead={lead}
        scope={scope}
        currentLoId={currentLoId}
        filter={filter}
        isSelected={selectedLeadId === lead.id}
        onSelect={onSelect}
      />
    </div>
  );
}

// Dancing Shoes-style grid: proportional fractional widths so the row
// feels compact and left-leaning. The Status column was removed
// (Feb 2026) because the priority/temp dot now renders directly under
// the borrower's name, making a separate Status pill redundant.
// Column order:
//   NAME · PHONE · STATE · LOAN PURPOSE · LOAN TYPE · RATE · LOAN AMOUNT
const LEADS_GRID = 'grid-cols-[1.9fr_1.2fr_0.5fr_1.3fr_1fr_0.9fr_1.1fr]';

function LeadsTableHeader({ filter, sort, setSort }) {
  // Same key names the body sort reducer expects — just a lighter
  // column-label strip to match the Grok queue.
  const cols = [
    { key: 'name',        label: 'Name',         align: 'left',   dir: 'asc'  },
    { key: 'phone',       label: 'Phone',        align: 'left',   dir: 'asc'  },
    { key: 'state',       label: 'State',        align: 'left',   dir: 'asc'  },
    { key: 'loanPurpose', label: 'Loan Purpose', align: 'left',   dir: 'asc'  },
    { key: 'loanType',    label: 'Loan Type',    align: 'left',   dir: 'asc'  },
    { key: 'rate',        label: 'Rate',         align: 'right',  dir: 'desc' },
    { key: 'loanAmount',  label: 'Loan Amount',  align: 'right',  dir: 'desc' },
  ];
  const cycle = (key, dflt) => {
    setSort((p) => p.key === key
      ? { key, dir: p.dir === dflt ? (dflt === 'desc' ? 'asc' : 'desc') : dflt }
      : { key, dir: dflt });
  };
  return (
    <div className={cn('hidden md:grid gap-x-4 px-4 pb-2 text-[10.5px] uppercase tracking-widest font-medium text-neutral-400', LEADS_GRID)}>
      {cols.map((c) => {
        const active = sort.key === c.key;
        const arrow = active ? (sort.dir === 'asc' ? '▲' : '▼') : '◇';
        return (
          <button
            key={c.key}
            type="button"
            onClick={() => cycle(c.key, c.dir)}
            data-testid={`leads-sort-${c.key}`}
            className={cn(
              // Explicit typography on the button itself — the browser's
              // user-agent stylesheet resets `text-transform: none` on
              // all `<button>` elements, which was swallowing the
              // parent's `uppercase` utility and rendering the headers
              // as "Name"/"Phone"/… instead of "NAME"/"PHONE"/… like
              // the Dancing Shoes queue. Restating the typography
              // utilities here guarantees the rendered headers match
              // GrokLeads exactly across both themes.
              'uppercase tracking-widest text-[10.5px] font-medium leading-[15.75px] text-neutral-400',
              'inline-flex items-center gap-1 select-none transition-colors hover:text-neutral-700 dark:hover:text-neutral-200',
              c.align === 'right'  && 'justify-end',
              c.align === 'center' && 'justify-center',
              c.align === 'left'   && 'justify-start',
            )}
          >
            {c.label}
            <span className={cn('text-[8.5px] leading-none', active ? 'text-neutral-700' : 'text-neutral-300')}>{arrow}</span>
          </button>
        );
      })}
    </div>
  );
}

const LeadRow = React.memo(function LeadRow({ lead, scope, currentLoId, filter, isSelected, onSelect }) {
  const navigate = useNavigate();
  // ── Click semantics (two actions on one row, per the spec) ──────
  // Row background → opens the right-side borrower summary panel.
  // Borrower name  → opens the EXISTING full lead profile route.
  // Interactive children (phone/email/pill/etc) already stopPropagation
  // so they don't fire either handler.
  const onRowClick = (e) => {
    // Guard against bubbled clicks from children that forgot to stop
    // propagation (e.g. a future action-menu nested deep in the row).
    if (e.defaultPrevented) return;
    if (onSelect) onSelect(lead.id);
    else navigate(`/leads/${lead.id}`);  // graceful fallback if used standalone
  };
  const onNameClick = (e) => {
    e.stopPropagation();
    navigate(`/leads/${lead.id}`);
  };
  const hasTemp = !!lead.temp;
  const TempIcon = hasTemp ? (TEMP_ICONS[lead.temp] || TEMP_ICONS.warm) : null;
  const styles = hasTemp ? (TEMP_STYLES[lead.temp] || TEMP_STYLES.warm) : null;
  // Honour both the modern leadTemps set and the legacy taxonomy so older
  // mock data still surfaces a proper label.
  const tempLabel = hasTemp
    ? ([...leadTemps, ...legacyLeadTemps].find((t) => t.key === lead.temp)?.label || lead.temp)
    : null;
  const state = deriveState(lead);
  // Treat empty strings on loan-type the same as missing so an outbound-
  // imported lead that captured `""` doesn't render an invisible cell.
  const rawLoanType = lead.application?.loanType;
  const loanType = rawLoanType && String(rawLoanType).trim() ? String(rawLoanType).trim() : '—';
  const rawLoanPurpose = formatLoanPurpose(lead.application?.loanPurpose || lead.loanIntent);
  const loanPurpose = rawLoanPurpose && rawLoanPurpose !== '—' ? rawLoanPurpose : null;
  // Rate — null/undefined/zero all collapse to the em-dash state so a
  // literal 0 doesn't masquerade as a real rate.
  const rateLabel = fmtRate(lead.application?.rate ?? lead.application?.loanRate);
  // Loan amount — prefer the CRM canonical `estAmount`, then the 1003's
  // `loanAmount`, finally `purchasePrice` for purchase-only imports.
  const loanAmount = pickLoanAmount(lead);
  // Avatar initials — DB `lead.initials` is authoritative when present,
  // but outbound-imported leads created before the backfill carry
  // `initials=null`. Derive from `name` in that case so the avatar
  // never renders empty.
  const avatarInitials = (lead.initials && String(lead.initials).trim())
    ? String(lead.initials).trim().toUpperCase()
    : initialsOf(lead.name);
  // Today's appointment? Drives the "Follow up today" badge under the name
  // and the row's float-to-top priority in the All tab.
  const apptToday = (() => {
    if (!lead.appointmentAt) return null;
    const d = new Date(lead.appointmentAt);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    return d.toDateString() === now.toDateString() ? d : null;
  })();
  // Only surface the "other LO" badge when scope = all AND this row isn't
  // assigned to the current user. In "my" scope every row is owned, so the
  // badge would be pure noise.
  const assignedToId = lead.assignedTo || null;
  const isOthers = scope === 'all' && assignedToId && assignedToId !== currentLoId;
  const assignee = isOthers ? TeamStore.get(assignedToId) : null;
  return (
    <div
      data-testid={`lead-row-${lead.id}`}
      className={cn(
        'grid gap-x-4 px-4 py-3 items-center group cursor-pointer transition-all duration-100 relative rounded-xl border bg-white dark:bg-[#18191C]',
        LEADS_GRID,
        isSelected
          ? 'border-sky-200 bg-sky-50/60 ring-2 ring-sky-100 dark:border-[hsl(var(--kf-gold)/0.5)] dark:bg-[#242119] dark:ring-1 dark:ring-[hsl(var(--kf-gold)/0.25)]'
          : apptToday
            ? 'border-[hsl(var(--kf-gold))]/40 bg-[hsl(var(--kf-gold-soft))]/30 hover:bg-[hsl(var(--kf-gold-soft))]/60'
            : 'border-neutral-200/80 hover:border-neutral-300 hover:shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:border-[#202126] dark:hover:border-[#303238] dark:hover:bg-[#1D1E22]'
      )}
      onClick={onRowClick}
    >
      {/* Name — avatar + clickable text + priority chip stacked below.
          Mirrors the Dancing Shoes queue row exactly: h-8 avatar w/ 11px
          initials, 13.5px semibold name, status/priority dot under the
          name. Row-click opens the right-side summary panel; clicking
          the name navigates to the existing /leads/:id full profile. */}
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-8 w-8 rounded-full bg-neutral-900 text-white text-[11px] font-semibold flex items-center justify-center shrink-0">
          {avatarInitials}
        </div>
        <div className="min-w-0">
          <button
            type="button"
            onClick={onNameClick}
            data-testid={`lead-row-name-${lead.id}`}
            className="text-left text-[13.5px] font-semibold text-neutral-900 truncate flex items-center gap-1.5 leading-tight hover:underline underline-offset-2"
            title="Open full profile"
          >
            <span className="truncate">{lead.name}</span>
            {lead.withdrawn && (
              <span className="inline-flex items-center gap-0.5 text-[8.5px] uppercase tracking-wider font-bold rounded px-1 py-0.5 bg-neutral-900 text-white shrink-0" title="Withdrawn from active pipeline">
                <Archive className="h-2 w-2" /> WD
              </span>
            )}
            {lead.denied && (
              <span className="inline-flex items-center gap-0.5 text-[8.5px] uppercase tracking-wider font-bold rounded px-1 py-0.5 bg-red-600 text-white shrink-0" title={`Denied — ${lead.deniedReason || 'reason not set'}`}>
                <ShieldX className="h-2 w-2" /> DN
              </span>
            )}
          </button>
          {/* Priority / temp dot under name — matches the Dancing Shoes
              row pattern (small colored dot + Title-case label). This
              now carries the sole status signal for the row (the former
              Status column was removed Feb 2026). */}
          <div className="mt-0.5">
            <LeadPriorityDot
              temp={lead.temp}
              apptToday={!!apptToday}
              tempLabel={tempLabel}
              isOthers={isOthers}
              assignee={assignee}
            />
          </div>
        </div>
      </div>

      {/* Phone — tel: link. stopPropagation so a click dials without
          also opening the right panel. */}
      <div className="text-[12.5px] text-neutral-700 truncate tabular-nums">
        {lead.phone ? (
          <a
            href={`tel:${(lead.phone || '').replace(/\D/g, '')}`}
            onClick={(e) => e.stopPropagation()}
            className="hover:text-neutral-900 hover:underline underline-offset-2"
            data-testid={`lead-row-phone-${lead.id}`}
          >
            {formatPhone(lead.phone)}
          </a>
        ) : (
          <span className="text-neutral-400">—</span>
        )}
      </div>

      {/* State */}
      <div className="text-[12.5px] text-neutral-700">
        {state && state !== '—' ? state : <span className="text-neutral-400">—</span>}
      </div>

      {/* Loan Purpose */}
      <div className="text-[12.5px] text-neutral-700 truncate" title={loanPurpose || ''}>
        {loanPurpose || <span className="text-neutral-400">—</span>}
      </div>

      {/* Loan Type */}
      <div className="text-[12.5px] text-neutral-700 truncate" title={loanType}>
        {loanType && loanType !== '—' ? loanType : <span className="text-neutral-400">—</span>}
      </div>

      {/* Rate — right-aligned tabular so decimals line up down the
          column. `fmtRate` collapses null/undefined/0 to the em-dash
          treatment per the Feb 2026 refinement (never render 0.000%). */}
      <div className="text-[12.5px] text-neutral-700 text-right tabular-nums">
        {rateLabel || <span className="text-neutral-400">—</span>}
      </div>

      {/* Loan Amount */}
      <div className="text-[12.5px] font-semibold text-neutral-900 text-right tabular-nums">
        {loanAmount ? money(loanAmount) : <span className="text-neutral-400 font-normal">—</span>}
      </div>
    </div>
  );
});

/* Small priority dot + label rendered under the borrower's name — the
   same visual pattern the Dancing Shoes queue uses. "Hot" / "Warm" /
   "Follow up today" each get their own color token so a scanner can
   read a row at a glance. In admin-side "All Leads" scope, this is
   also where the "assigned to another LO" tag surfaces (the former
   Status column carried that signal before Feb 2026). */
function LeadPriorityDot({ temp, apptToday, tempLabel, isOthers, assignee }) {
  if (apptToday) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--kf-gold-deep))]">
        <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--kf-gold))]" />
        Follow up today
      </span>
    );
  }
  if (!temp) {
    // Fall back to the LO assignment badge so admins viewing All Leads
    // still see "which broker owns this row" at a glance.
    if (isOthers && assignee) {
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[hsl(var(--kf-gold-deep))]">
          <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--kf-gold))]" />
          {assignee.name?.split(' ')[0] || assignee.initials}
        </span>
      );
    }
    return <span className="text-[11px] text-neutral-400">—</span>;
  }
  const dotMap = {
    hot:          'bg-red-500',
    warm:         'bg-amber-500',
    cold:         'bg-sky-500',
    nurture:      'bg-neutral-400',
    dnc:          'bg-neutral-700',
    pre_approved: 'bg-emerald-500',
    strike_rate:  'bg-indigo-500',
    second_voice: 'bg-sky-500',
  };
  const textMap = {
    hot:          'text-red-600',
    warm:         'text-amber-700',
    cold:         'text-sky-700',
    nurture:      'text-neutral-500',
    dnc:          'text-neutral-700',
    pre_approved: 'text-emerald-700',
    strike_rate:  'text-indigo-700',
    second_voice: 'text-sky-700',
  };
  return (
    <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium', textMap[temp] || 'text-neutral-500')}>
      <span className={cn('h-1.5 w-1.5 rounded-full', dotMap[temp] || 'bg-neutral-400')} />
      {tempLabel}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Filters popover — opens from the "Filters" button in the header.   */
/* Lets the LO narrow leads by State, Loan Purpose, Loan Amount       */
/* range, plus toggle the withdrawn-leads view.                       */
/* ------------------------------------------------------------------ */
function FiltersPopover({ adv, setAdv, stateOptions, purposeOptions, onClose, onReset }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const toggleArr = (key, val) => {
    setAdv((a) => {
      const list = a[key];
      return { ...a, [key]: list.includes(val) ? list.filter((x) => x !== val) : [...list, val] };
    });
  };

  return (
    <div
      ref={ref}
      data-testid="leads-filters-popover"
      className="absolute z-30 top-full mt-2 right-0 w-[380px] rounded-2xl border hairline bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.25)] overflow-hidden"
    >
      <div className="px-4 py-3 border-b hairline flex items-center justify-between">
        <div className="text-[12.5px] font-bold text-neutral-900">Refine leads</div>
        <button
          onClick={onClose}
          className="h-7 w-7 rounded-md hover:bg-neutral-100 flex items-center justify-center text-neutral-500"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-5 max-h-[480px] overflow-y-auto">
        {/* State */}
        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-2">State</div>
          {stateOptions.length === 0 ? (
            <div className="text-[11.5px] text-neutral-400 italic">No state data on visible leads.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {stateOptions.map((s) => {
                const active = adv.states.includes(s);
                return (
                  <button
                    key={s}
                    data-testid={`leads-filter-state-${s}`}
                    onClick={() => toggleArr('states', s)}
                    className={cn(
                      'text-[11.5px] font-bold rounded-md px-2 py-1 border transition tabular-nums',
                      active
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
                    )}
                  >{s}</button>
                );
              })}
            </div>
          )}
        </div>

        {/* Loan Purpose */}
        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Loan purpose</div>
          {purposeOptions.length === 0 ? (
            <div className="text-[11.5px] text-neutral-400 italic">No loan purpose data on visible leads.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {purposeOptions.map((p) => {
                const active = adv.purposes.includes(p);
                return (
                  <button
                    key={p}
                    data-testid={`leads-filter-purpose-${p.replace(/\W+/g, '-').toLowerCase()}`}
                    onClick={() => toggleArr('purposes', p)}
                    className={cn(
                      'text-[11.5px] font-semibold rounded-md px-2 py-1 border transition',
                      active
                        ? 'bg-neutral-900 text-white border-neutral-900'
                        : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
                    )}
                  >{p}</button>
                );
              })}
            </div>
          )}
        </div>

        {/* Loan Amount */}
        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Loan amount</div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-bold">$</span>
              <input
                data-testid="leads-filter-min-amount"
                value={adv.minAmount}
                onChange={(e) => setAdv((a) => ({ ...a, minAmount: e.target.value.replace(/[^0-9]/g, '') }))}
                placeholder="Min"
                inputMode="numeric"
                className="w-full h-9 pl-6 pr-2 rounded-md border hairline bg-white text-[12.5px] focus:outline-none focus:border-neutral-900"
              />
            </div>
            <span className="text-neutral-400 text-[12px]">–</span>
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-bold">$</span>
              <input
                data-testid="leads-filter-max-amount"
                value={adv.maxAmount}
                onChange={(e) => setAdv((a) => ({ ...a, maxAmount: e.target.value.replace(/[^0-9]/g, '') }))}
                placeholder="Max"
                inputMode="numeric"
                className="w-full h-9 pl-6 pr-2 rounded-md border hairline bg-white text-[12.5px] focus:outline-none focus:border-neutral-900"
              />
            </div>
          </div>
        </div>

        {/* Lifecycle toggles */}
        <div className="space-y-2">
          <button
            data-testid="leads-filter-show-withdrawn"
            onClick={() => setAdv((a) => ({ ...a, showWithdrawn: !a.showWithdrawn }))}
            className={cn(
              'w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition text-left',
              adv.showWithdrawn ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'
            )}
          >
            <div className="flex items-start gap-2">
              <Archive className="h-4 w-4 mt-0.5 text-neutral-700" />
              <div>
                <div className="text-[12.5px] font-bold text-neutral-900">Show withdrawn leads</div>
                <div className="text-[10.5px] text-neutral-500 mt-0.5">Hidden from active pipeline by default.</div>
              </div>
            </div>
            <span className={cn(
              'h-5 w-9 rounded-full relative transition',
              adv.showWithdrawn ? 'bg-neutral-900' : 'bg-neutral-200'
            )}>
              <span className={cn(
                'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition',
                adv.showWithdrawn ? 'left-[18px]' : 'left-0.5'
              )} />
            </span>
          </button>

          <button
            data-testid="leads-filter-show-denied"
            onClick={() => setAdv((a) => ({ ...a, showDenied: !a.showDenied }))}
            className={cn(
              'w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 transition text-left',
              adv.showDenied ? 'border-red-300 bg-red-50/50' : 'border-neutral-200 hover:border-neutral-400'
            )}
          >
            <div className="flex items-start gap-2">
              <ShieldX className="h-4 w-4 mt-0.5 text-red-600" />
              <div>
                <div className="text-[12.5px] font-bold text-neutral-900">Show denied leads</div>
                <div className="text-[10.5px] text-neutral-500 mt-0.5">Hidden from active pipeline by default.</div>
              </div>
            </div>
            <span className={cn(
              'h-5 w-9 rounded-full relative transition',
              adv.showDenied ? 'bg-red-600' : 'bg-neutral-200'
            )}>
              <span className={cn(
                'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition',
                adv.showDenied ? 'left-[18px]' : 'left-0.5'
              )} />
            </span>
          </button>
        </div>
      </div>

      <div className="px-4 py-3 border-t hairline flex items-center justify-between bg-neutral-50/60">
        <button
          data-testid="leads-filters-reset"
          onClick={onReset}
          className="text-[11.5px] font-semibold text-neutral-500 hover:text-neutral-900 transition"
        >Reset all</button>
        <button
          data-testid="leads-filters-done"
          onClick={onClose}
          className="h-8 px-3 rounded-md btn-ink text-[11.5px] font-bold"
        >Done</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* AssigneeFilter — admin-only dropdown shown when the admin is in    */
/* "All Leads" scope. Narrows the visible pool to one LO (or the      */
/* unassigned bucket).                                                */
/* ------------------------------------------------------------------ */
function AssigneeFilter({ value, onChange, members }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // LOs are role = lo; admins still get listed for completeness but pushed
  // to the bottom so the broker team reads first.
  const sorted = useMemo(() => {
    const list = (members || []).filter((m) => m.is_active !== false);
    return list.sort((a, b) => {
      if (a.role !== b.role) return a.role === 'lo' ? -1 : 1;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }, [members]);

  const selected =
    value === 'all'        ? { label: 'All Loan Officers', sub: `${sorted.length} brokers` } :
    value === 'unassigned' ? { label: 'Unassigned',         sub: 'No LO yet' } :
                             (() => {
                               const m = sorted.find((x) => x.id === value);
                               return m ? { label: m.name, sub: m.title || (m.role === 'admin' ? 'Administrator' : 'Broker') }
                                        : { label: 'All Loan Officers', sub: '' };
                             })();

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="leads-assignee-filter-trigger"
        className={cn(
          'h-9 px-3 rounded-lg border hairline bg-white hover:border-neutral-400 inline-flex items-center gap-2 text-[12.5px] font-semibold transition',
          value !== 'all' ? 'border-neutral-900 text-neutral-900' : 'text-neutral-700',
        )}
      >
        <span className="truncate max-w-[160px]">{selected.label}</span>
        <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />
      </button>
      {open && (
        <div
          data-testid="leads-assignee-filter-menu"
          className="absolute right-0 top-full mt-1.5 z-30 w-[240px] rounded-xl border hairline bg-white shadow-[0_18px_48px_-18px_rgba(0,0,0,0.25)] overflow-hidden"
        >
          <div className="py-1 max-h-[320px] overflow-y-auto">
            <FilterOption
              active={value === 'all'}
              onClick={() => { onChange('all'); setOpen(false); }}
              label="All Loan Officers"
              sub={`${sorted.length} brokers`}
              testId="leads-assignee-filter-all"
            />
            <FilterOption
              active={value === 'unassigned'}
              onClick={() => { onChange('unassigned'); setOpen(false); }}
              label="Unassigned"
              sub="No LO yet"
              testId="leads-assignee-filter-unassigned"
            />
            <div className="my-1 mx-3 border-t hairline" />
            {sorted.length === 0 ? (
              <div className="px-3 py-2 text-[11.5px] italic text-neutral-400">No team members yet.</div>
            ) : sorted.map((m) => (
              <FilterOption
                key={m.id}
                active={value === m.id}
                onClick={() => { onChange(m.id); setOpen(false); }}
                label={m.name}
                sub={m.title || (m.role === 'admin' ? 'Administrator' : 'Broker')}
                testId={`leads-assignee-filter-${m.id}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterOption({ active, onClick, label, sub, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'w-full text-left px-3 py-2 transition flex items-center justify-between gap-3',
        active ? 'bg-neutral-50 text-neutral-900' : 'text-neutral-700 hover:bg-neutral-50',
      )}
    >
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold truncate">{label}</div>
        <div className="text-[10.5px] text-neutral-500 truncate">{sub}</div>
      </div>
      {active && <span className="h-1.5 w-1.5 rounded-full bg-neutral-900 shrink-0" />}
    </button>
  );
}


